import { Box, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { countryList } from '../../../constants/countries';
import { selectContentBackgroundColor } from '../../../store/selectors/derived';
import { IFixedAddress } from '../../../types/api';
import { getPrimaryTextColor } from '../../../util/ui';

interface IProps {
  address?: IFixedAddress;
}

export const FixedAddress: React.FC<IProps> = ({ address }) => {
  const { label, address1, address2, city, state, country: countryCode, zip, company_name } = address || {};

  const bgColor = useSelector(selectContentBackgroundColor);

  const countryName = useMemo(
    () => countryList.find(({ two_digit_code }) => two_digit_code === countryCode)?.name || countryCode,
    [countryCode],
  );

  const textColor = useMemo(() => {
    return getPrimaryTextColor(bgColor);
  }, [bgColor]);

  // Only non-empty lines are rendered: the previous template strings emitted trailing spaces and
  // commas, and produced blank rows (a bare "  " city line) when a field was missing.
  // Keyed by field, not by text — line values legitimately repeat (label === company_name, or
  // address1 === address2), and keying on content collides.
  const lines = useMemo(
    () =>
      [
        { id: 'label', text: label },
        { id: 'company_name', text: company_name },
        { id: 'address1', text: address1 },
        { id: 'address2', text: address2 },
        { id: 'city_state_country', text: [city, state, countryName].filter(Boolean).join(', ') },
        { id: 'zip', text: zip },
      ].filter((line): line is { id: string; text: string } => !!line.text),
    [label, company_name, address1, address2, city, state, countryName, zip],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {lines.map(({ id, text }) => (
        <Typography key={id} data-testid="fixed-address-line" variant="body1" color={textColor}>
          {text}
        </Typography>
      ))}
    </Box>
  );
};
